import React from "react";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  linearProgressClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import WorkspacesService from "../../../services/WorkspacesService";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import { CURRENT_USER_QUERY_KEY } from "../../../wrappers/current-user/CurrentUserWrapper";
import { useNavigate } from "react-router-dom";
import { COACH_PLAN_ROUTE } from "../../../routes/routes";
import Lottie from "react-lottie";
import confettiAnimation from "../../../lotties/confetti-hover-pinch.json";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { InvoiceBreakdownDialog } from "../../dialog/InvoiceBreakdownDialog";
import {
  AddonCode,
  AddonUpdateDto,
} from "@growth-machine-llc/stridist-api-client";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { Plan } from "../../../constants";

const PUBLISHING_STAGES = [
  { label: "Initializing deployment...", duration: 2500 },
  { label: "Verifying app bundle...", duration: 1400 },
  { label: "Transferring clients...", duration: 3700 },
  { label: "Adding branded app to your subscription...", duration: 1100 },
  { label: "Finalizing release...", duration: 4800 },
];

const COMPLETE_STAGE_LABEL = "Published successfully!";

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 12,
  borderRadius: 6,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  "&.MuiLinearProgress-colorSuccess": {
    backgroundColor: theme.palette.grey[200],
  },
  [`&.${linearProgressClasses.colorPrimary} .${linearProgressClasses.bar}`]: {
    borderRadius: 6,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  },
  [`&.MuiLinearProgress-colorSuccess .${linearProgressClasses.bar}`]: {
    borderRadius: 6,
    background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
  },
}));

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.common.black,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.common.black,
  },
  goLiveContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
    position: "relative",
  },

  goLiveButton: {
    width: "100%",
    minHeight: 72,
    fontSize: 24,
    fontWeight: 700,
    borderRadius: theme.spacing(1),
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    boxShadow: "none",

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: "none",
    },

    "&:disabled": {
      backgroundColor: theme.palette.grey[400],
    },

    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(1.5),
      "& svg": {
        fontSize: 32,
      },
    },
  },
  goLiveSubtext: {
    marginTop: theme.spacing(2),
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  paymentRequiredText: {
    marginTop: theme.spacing(2),
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.warning.main,
    textAlign: "center",
    maxWidth: 500,
  },

  progressContainer: {
    width: "100%",
    padding: theme.spacing(4, 3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${theme.palette.divider}`,
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  progressPercentage: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  progressBarWrapper: {
    marginBottom: theme.spacing(2),
  },
  progressStage: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    minHeight: 20,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  progressDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    animation: "$pulse 1s ease-in-out infinite",
  },
  progressDotComplete: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.success.main,
  },
  progressStageComplete: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.success.main,
    minHeight: 20,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  progressContainerComplete: {
    width: "100%",
    padding: theme.spacing(4, 3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${theme.palette.success.main}`,
    position: "relative",
    overflow: "hidden",
  },
  confettiFullPage: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 10,
    display: "flex",
    alignItems: "center",

    "& > div": {
      maxWidth: "80%",
      maxHeight: "80%",
      aspectRatio: "1 / 1",
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.5,
      transform: "scale(0.8)",
    },
  },
}));

export function ReadyForPublishContent() {
  const s = useStyles();
  const { brandName } = useCurrentBrand();
  const { showToastAlert } = useToastAlert();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showConfetti, setShowConfetti] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);
  const [currentStage, setCurrentStage] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const [showInvoiceDialog, setShowInvoiceDialog] = React.useState(false);
  const mutationStartTime = React.useRef<number>(0);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const stageTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { plan } = useCurrentUser();

  const confettiOptions = {
    loop: false,
    autoplay: true,
    animationData: confettiAnimation,
  };

  const totalDuration = PUBLISHING_STAGES.reduce(
    (sum, stage) => sum + stage.duration,
    0,
  );

  const stopProgressSimulation = React.useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (stageTimeoutRef.current) {
      clearTimeout(stageTimeoutRef.current);
      stageTimeoutRef.current = null;
    }
  }, []);

  const startProgressSimulation = React.useCallback(() => {
    setCurrentStage(0);
    setProgress(0);

    let currentStageIndex = 0;

    const advanceStage = () => {
      currentStageIndex++;

      if (currentStageIndex >= PUBLISHING_STAGES.length) {
        return;
      }

      setCurrentStage(currentStageIndex);

      const progressPerStage = 95 / PUBLISHING_STAGES.length;
      const newProgress = Math.min(progressPerStage * currentStageIndex, 95);
      setProgress(newProgress);

      stageTimeoutRef.current = setTimeout(
        advanceStage,
        PUBLISHING_STAGES[currentStageIndex].duration,
      );
    };

    stageTimeoutRef.current = setTimeout(
      advanceStage,
      PUBLISHING_STAGES[0].duration,
    );
  }, []);

  const completeProgress = React.useCallback(() => {
    stopProgressSimulation();
    setProgress(100);
    setIsComplete(true);
  }, [stopProgressSimulation]);

  React.useEffect(() => {
    return () => {
      stopProgressSimulation();
    };
  }, [stopProgressSimulation]);

  const { mutate: publishBrandedApp } = useMutation({
    mutationKey: ["publish-branded-app"],
    mutationFn: WorkspacesService.publishBrandedApp,
    onSuccess: (result) => {
      const elapsed = Date.now() - mutationStartTime.current;
      const minProgressTime = totalDuration;
      const remainingDelay = Math.max(0, minProgressTime - elapsed);

      setTimeout(() => {
        completeProgress();

        if (result.success) {
          setShowConfetti(true);

          showToastAlert("success", {
            message: result.message || "Your branded app is now live!",
          });

          setTimeout(async () => {
            setShowConfetti(false);
            await queryClient.refetchQueries({
              queryKey: [CURRENT_USER_QUERY_KEY],
            });
            setIsLoading(false);
          }, 4000);
        } else if (result.requiresPayment) {
          setIsLoading(false);
          showToastAlert("warning", {
            message:
              result.message ||
              "Please activate the Custom Branded App addon to publish your app.",
          });
          navigate(COACH_PLAN_ROUTE);
        } else {
          setIsLoading(false);
          showToastAlert("error", {
            message: result.message || "Failed to publish branded app.",
          });
        }
      }, remainingDelay);
    },
    onError: (error: Error) => {
      const elapsed = Date.now() - mutationStartTime.current;
      const minProgressTime = totalDuration;
      const remainingDelay = Math.max(0, minProgressTime - elapsed);

      setTimeout(() => {
        stopProgressSimulation();
        setIsLoading(false);
        setProgress(0);
        setCurrentStage(0);
        setIsComplete(false);
        showToastAlert("error", {
          message: error.message || "An error occurred while publishing.",
        });
      }, remainingDelay);
    },
  });

  const handleGoLiveClick = () => {
    setShowInvoiceDialog(true);
  };

  const handleInvoiceClose = () => {
    setShowInvoiceDialog(false);
  };

  const handleInvoiceConfirm = () => {
    setShowInvoiceDialog(false);
    if (isLoading) return;
    mutationStartTime.current = Date.now();
    setIsLoading(true);
    startProgressSimulation();
    publishBrandedApp();
  };

  return (
    <>
      {showConfetti && (
        <Box className={s.confettiFullPage}>
          <Lottie options={confettiOptions} />
        </Box>
      )}
      <Box className={s.header}>
        <Box className={s.titleBox}>
          <Typography className={s.title}>{brandName} Branded App</Typography>
          <Box className={s.statusBox}>
            <Typography className={s.status}>ready for publishing</Typography>
          </Box>
        </Box>

        <Typography className={s.text}>
          Congratulations! Your branded app has been built and is ready to go
          live. The app has been submitted to both the Apple App Store and
          Google Play Store.
        </Typography>

        <Typography className={s.text} sx={{ marginTop: 2 }}>
          Click the button below to make your app available to your clients.
          Once published, your clients will be able to download and use your
          custom branded app!
        </Typography>

        <Box className={s.goLiveContainer}>
          {!isLoading ? (
            <>
              <Button
                className={s.goLiveButton}
                variant="contained"
                onClick={handleGoLiveClick}
                startIcon={<RocketLaunchIcon />}
              >
                Go Live!
              </Button>
              <Typography className={s.goLiveSubtext}>
                This will publish your app and make it available to your
                clients. The Custom Branded App addon will be added to your
                subscription.
              </Typography>
            </>
          ) : (
            <Box
              className={
                isComplete ? s.progressContainerComplete : s.progressContainer
              }
            >
              <Box className={s.progressHeader}>
                <Typography className={s.progressTitle}>
                  {isComplete
                    ? "Publishing complete!"
                    : "Publishing your app..."}
                </Typography>
                <Typography className={s.progressPercentage}>
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <Box className={s.progressBarWrapper}>
                <StyledLinearProgress
                  variant="determinate"
                  value={progress}
                  color={isComplete ? "success" : "primary"}
                />
              </Box>
              <Typography
                className={
                  isComplete ? s.progressStageComplete : s.progressStage
                }
              >
                <span
                  className={isComplete ? s.progressDotComplete : s.progressDot}
                />
                {isComplete
                  ? COMPLETE_STAGE_LABEL
                  : PUBLISHING_STAGES[currentStage]?.label}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <InvoiceBreakdownDialog
        open={showInvoiceDialog}
        plan={plan as Plan}
        addons={[
          AddonUpdateDto.fromJS({ addonCode: AddonCode.CBA, quantity: 1 }),
        ]}
        onClose={handleInvoiceClose}
        onConfirm={handleInvoiceConfirm}
      />
    </>
  );
}
